import { IncomingMessage, ServerResponse } from 'http'

import { useMemo } from 'react'

import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, NormalizedCacheObject, split } from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { onError } from '@apollo/client/link/error'

import { deepMerge, getQaPrLink } from '@local/utils'

import { initializeFederationApollo } from '@web/libs/apollo/federationClient'
import introspection from 'types/possible-types'

import { IS_BROWSER, IS_PR_QA_MODE, PHASE, PR_NUMBER } from '../constants'
import links from '../constants/links'
import { IS_SSR, neloLogger } from '../utils'
import typePolicies from './typePolicies'

export let apolloClient: ApolloClient<NormalizedCacheObject> | undefined

export type ResolverContext = {
  req?: IncomingMessage
  res?: ServerResponse
}

function createIsomorphLink(context: ResolverContext = {}) {
  const cookie = context?.req?.headers?.cookie

  const onFetch: typeof fetch = (input, init) => {
    const page = IS_BROWSER ? window.location.href : context.req?.headers?.['x-original-url']?.toString()

    const customHeaders = {
      ...(page ? { page } : undefined),
      ssr: IS_SSR ? 'true' : 'false',
    }

    if (init) init.headers = { ...init.headers, ...customHeaders }

    return fetch(input, init)
  }

  if (IS_PR_QA_MODE) {
    return new HttpLink({
      uri: `${getQaPrLink(PR_NUMBER).MYPLACE_API}/graphql`,
      ...(!IS_BROWSER ? { headers: cookie ? { cookie } : undefined } : { credentials: 'include' }),
      fetch: onFetch,
    })
  }

  if (IS_BROWSER)
    return new BatchHttpLink({
      uri: `${links.API_URL_ON_BROWSER}/graphql`,
      credentials: 'include',
      batchMax: 20,
      batchInterval: 30,
      fetch: onFetch,
    })

  return new HttpLink({
    uri: `${links.API_URL_ON_NODE}/graphql`,
    headers: cookie ? { cookie } : undefined,
    fetch: onFetch,
  })
}

const createFileApiLink = (context: ResolverContext = {}) => {
  const cookie = context.req?.headers?.cookie

  if (IS_BROWSER)
    return new BatchHttpLink({
      uri: `${links.FILE_API}/graphql`,
      credentials: 'include',
      batchMax: 20,
      batchInterval: 30,
    })

  return new HttpLink({
    uri: `${links.FILE_API}/graphql`,
    headers: cookie ? { cookie } : undefined,
  })
}

export const createErrorLink = (context: ResolverContext = {}) => {
  const userAgent = IS_BROWSER ? window.navigator.userAgent : context?.req?.headers['user-agent'] ?? '알 수 없음'
  const page = IS_BROWSER ? window.location.href : context.req?.headers?.['x-original-url']?.toString()

  return onError(({ graphQLErrors, networkError, operation }) => {
    const message = `apollo error - ${operation.operationName}`

    const metaData = {
      userAgent,
      graphQLErrors,
      page,
      ssr: IS_SSR ? 'true' : 'false',
      operation: {
        name: operation.operationName,
        variables: operation.variables,
      },
      ...(networkError && {
        networkError: {
          message: networkError.message,
          name: networkError.name,
          cause: networkError.cause,
          stack: networkError.stack,
        },
      }),
    }

    // eslint-disable-next-line no-console
    if (PHASE === 'development') console.error(message, metaData)

    neloLogger('error', message, metaData).catch(null)
  })
}

export const offlineLink = new ApolloLink((operation, forward) => {
  if (IS_BROWSER && !window.navigator.onLine) {
    // 오프라인 상태일 때 요청을 차단하거나 캐싱된 데이터를 반환합니다.
    return null
  }

  return forward(operation)
})

// INFO: 추후 mutation, query 분기가 필요할 경우 사용
const operationNamesUsingFileApi = new Set<string>()

function createApolloClient(context?: ResolverContext) {
  const profileApiLink = createIsomorphLink(context)
  const fileApiLink = createFileApiLink(context)
  const errorLink = createErrorLink(context)

  const endpointLink = split(
    operation => operationNamesUsingFileApi.has(operation.operationName),
    fileApiLink,
    profileApiLink,
  )

  return new ApolloClient({
    ssrMode: !IS_BROWSER,
    link: from([offlineLink, errorLink, endpointLink]),
    cache: new InMemoryCache({
      typePolicies,
      possibleTypes: introspection.possibleTypes,
      addTypename: true,
    }),
    connectToDevTools: PHASE !== 'production',
  })
}

interface initializeApolloOption {
  initialState?: NormalizedCacheObject
  // Pages with Next.js data fetching methods, like `getStaticProps`, can send
  // a custom context which will be used by `SchemaLink` to server render pages
  context?: ResolverContext
}

export function initializeApollo({ initialState, context }: initializeApolloOption = {}) {
  const _apolloClient = apolloClient ?? createApolloClient(context)

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  if (initialState) {
    const existingCache = _apolloClient.extract()
    _apolloClient.cache.restore(deepMerge(existingCache, initialState)) // Merge exisiting cache with data from SSR
  }

  // For SSG and SSR always create a new Apollo Client
  if (!IS_BROWSER) return _apolloClient

  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient

  return _apolloClient
}

export function useApollo(initialState?: NormalizedCacheObject, federation: boolean = false) {
  const store = useMemo(() => initializeApollo({ initialState }), [initialState])
  const federatedStore = useMemo(() => initializeFederationApollo({ initialState }), [initialState])

  if (federation) return federatedStore

  return store
}
